import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { RateRow } from "@/lib/villa-content";

export type PricingSettings = {
  rates: RateRow[];
  lastMinuteEnabled: boolean;
  lastMinutePercent: number;
  lastMinuteWithinDays: number;
  /** Optional explicit end date (YYYY-MM-DD). When set, overrides the rolling withinDays cutoff. */
  lastMinuteEndDate: string | null;
  lastMinuteLabel: { en: string; nl: string; fr: string; de: string };
  /** Optional custom banner text per language. Empty string = use the built-in default. */
  lastMinuteText: { en: string; nl: string; fr: string; de: string };
  /** Price per extra m³ of water (€), used in contract balance. */
  waterPricePerM3: number;
  /**
   * Custom date ranges (YYYY-MM-DD, half-open [start, end)) that admins
   * have opened up: within these ranges the 5-day lead-time rule and the
   * Saturday-only rule (July/August) are ignored.
   */
  leadTimeOverrides: Array<{ start: string; end: string; label?: string; enabled?: boolean }>;
};

const PERIOD_RE = /^\d{2}\.\d{2}\.\d{4}\s*[-–]\s*\d{2}\.\d{2}\.\d{4}$/;

const RateRowSchema = z.object({
  period: z.string().min(1).max(64).regex(PERIOD_RE, "Format: dd.mm.yyyy - dd.mm.yyyy"),
  beds2: z.number().int().min(0).max(100000).nullable(),
  beds3: z.number().int().min(0).max(100000).nullable(),
  beds4: z.number().int().min(0).max(100000).nullable(),
  water2: z.number().min(0).max(1000).nullable().optional(),
  water3: z.number().min(0).max(1000).nullable().optional(),
  water4: z.number().min(0).max(1000).nullable().optional(),
});

const SettingsSchema = z.object({
  rates: z.array(RateRowSchema).min(0).max(40),
  lastMinuteEnabled: z.boolean(),
  lastMinutePercent: z.number().min(0).max(90),
  lastMinuteWithinDays: z.number().int().min(0).max(365),
  lastMinuteEndDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
    .nullable(),
  lastMinuteLabel: z.object({
    en: z.string().min(1).max(80),
    nl: z.string().min(1).max(80),
    fr: z.string().min(1).max(80),
    de: z.string().min(1).max(80),
  }),
  lastMinuteText: z.object({
    en: z.string().max(240),
    nl: z.string().max(240),
    fr: z.string().max(240),
    de: z.string().max(240),
  }),
  waterPricePerM3: z.number().min(0).max(100),
  leadTimeOverrides: z
    .array(
      z
        .object({
          start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD"),
          end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD"),
          label: z.string().trim().max(80).optional(),
          enabled: z.boolean().optional().default(true),
        })
        .refine((r) => r.end > r.start, { message: "Einddatum moet na de startdatum liggen" }),
    )
    .max(50),
});

type Row = {
  rates: unknown;
  last_minute_enabled: boolean;
  last_minute_percent: number | string;
  last_minute_within_days: number;
  last_minute_end_date: string | null;
  last_minute_label_en: string;
  last_minute_label_nl: string;
  last_minute_label_fr: string;
  last_minute_label_de: string;
  last_minute_text_en?: string | null;
  last_minute_text_nl?: string | null;
  last_minute_text_fr?: string | null;
  last_minute_text_de?: string | null;
  water_price_per_m3: number | string | null;
  lead_time_overrides: unknown;
};

function rowToSettings(row: Row): PricingSettings {
  const ratesParsed = z.array(RateRowSchema).safeParse(row.rates);
  const overridesParsed = SettingsSchema.shape.leadTimeOverrides.safeParse(row.lead_time_overrides);
  return {
    rates: ratesParsed.success ? ratesParsed.data : [],
    lastMinuteEnabled: !!row.last_minute_enabled,
    lastMinutePercent: Number(row.last_minute_percent) || 0,
    lastMinuteWithinDays: Number(row.last_minute_within_days) || 0,
    lastMinuteEndDate: row.last_minute_end_date ?? null,
    lastMinuteLabel: {
      en: row.last_minute_label_en,
      nl: row.last_minute_label_nl,
      fr: row.last_minute_label_fr,
      de: row.last_minute_label_de,
    },
    lastMinuteText: {
      en: row.last_minute_text_en ?? "",
      nl: row.last_minute_text_nl ?? "",
      fr: row.last_minute_text_fr ?? "",
      de: row.last_minute_text_de ?? "",
    },
    waterPricePerM3: Number(row.water_price_per_m3 ?? 4) || 4,
    leadTimeOverrides: overridesParsed.success ? overridesParsed.data : [],
  };
}

/** Public — read pricing settings (rates + last-minute deal). */
export const getPricingSettings = createServerFn({ method: "GET" }).handler(
  async (): Promise<PricingSettings> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("pricing_settings")
      .select(
        "rates,last_minute_enabled,last_minute_percent,last_minute_within_days,last_minute_end_date,last_minute_label_en,last_minute_label_nl,last_minute_label_fr,last_minute_label_de,last_minute_text_en,last_minute_text_nl,last_minute_text_fr,last_minute_text_de,water_price_per_m3,lead_time_overrides",
      )
      .eq("id", "singleton")
      .maybeSingle();
    if (error) throw new Error("Failed to load pricing settings");
    if (!data) {
      return {
        rates: [],
        lastMinuteEnabled: false,
        lastMinutePercent: 0,
        lastMinuteWithinDays: 30,
        lastMinuteEndDate: null,
        lastMinuteLabel: {
          en: "Last-minute deal",
          nl: "Last-minute deal",
          fr: "Offre de dernière minute",
          de: "Last-Minute-Angebot",
        },
        lastMinuteText: { en: "", nl: "", fr: "", de: "" },
        waterPricePerM3: 4,
        leadTimeOverrides: [],
      };
    }
    return rowToSettings(data as Row);
  },
);

/** Admin — update pricing settings. */
export const updatePricingSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SettingsSchema.parse(input))
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { error } = await supabaseAdmin.from("pricing_settings").upsert(
      {
        id: "singleton",
        rates: data.rates,
        last_minute_enabled: data.lastMinuteEnabled,
        last_minute_percent: data.lastMinutePercent,
        last_minute_within_days: data.lastMinuteWithinDays,
        last_minute_end_date: data.lastMinuteEndDate,
        last_minute_label_en: data.lastMinuteLabel.en,
        last_minute_label_nl: data.lastMinuteLabel.nl,
        last_minute_label_fr: data.lastMinuteLabel.fr,
        last_minute_label_de: data.lastMinuteLabel.de,
        last_minute_text_en: data.lastMinuteText.en,
        last_minute_text_nl: data.lastMinuteText.nl,
        last_minute_text_fr: data.lastMinuteText.fr,
        last_minute_text_de: data.lastMinuteText.de,
        water_price_per_m3: data.waterPricePerM3,
        lead_time_overrides: data.leadTimeOverrides,
      },
      { onConflict: "id" },
    );
    if (error) throw new Error(error.message);

    try {
      const { writeAuditLog } = await import("./audit.server");
      await writeAuditLog({
        actorId: context.userId,
        actorEmail: null,
        action: "pricing.updated",
        entity: "pricing_settings",
        entityId: "singleton",
        metadata: {
          rate_rows: data.rates.length,
          last_minute_enabled: data.lastMinuteEnabled,
          last_minute_percent: data.lastMinutePercent,
        },
      });
    } catch (e) {
      console.error("[updatePricingSettings] audit failed", e);
    }
    return { ok: true };
  });